import { OperationVariables, useApolloClient } from '@apollo/client'
import { DocumentNode } from 'graphql'
import jsPDF from 'jspdf'
import { useCallback } from 'react'
import { AWS_S3_RESIZED_URL, AWS_S3_URL, TYPE_DE_VOIE } from './constants'
import { D4_Conseilsyndical, D4_Copropriete, D4_Personne } from './types'

export function useImperativeQuery<TData = any, TVariables = OperationVariables>(query: DocumentNode) {
  const client = useApolloClient()
  return useCallback(
    (variables: TVariables) =>
      client.query<TData, TVariables>({
        query: query,
        variables: variables,
      }),
    [client, query]
  )
}

export const generateLegacyLink = ({ path, url, id }: { path: string; url: string | undefined; id: number }) => {
  return url + '/legacy/' + path + `${id > 0 ? '?id=' + id : ''}`
}

interface StringSpacer {
  cur: number
  spacer: string
}

export const stringSpacer = (options: StringSpacer[], value: string) => {
  const s = value.split('')
  options.forEach((opt) => s.splice(opt.cur, 0, opt.spacer))
  return s.join('')
}

export const RAR_SPACER = [
  { cur: 2, spacer: ' ' },
  { cur: 6, spacer: ' ' },
  { cur: 10, spacer: ' ' },
  { cur: 15, spacer: ' ' },
]

export const format = {
  rar: (num: string) => stringSpacer(RAR_SPACER, num),
}

export const s3Image = (name: string | undefined) => {
  if (!name) return
  return `${AWS_S3_URL}${name}`
}

export const s3ThumbImage = (name: string) => {
  if (!name) return
  return `${AWS_S3_RESIZED_URL}resized-${name}.png`
}

export const getPdfDoc = async ({ callback, id }: { callback: (doc: jsPDF) => void; id: string }) => {
  const doc = new jsPDF('p', 'pt', 'a4', true)
  const pdf = document.getElementById('pdf') as HTMLElement

  await doc.html(pdf, {
    callback,
    margin: [0, 0, 0, 0],
    x: 0,
    y: 0,
  })

  return doc
}

export const generateBordereau = async () => {
  await getPdfDoc({
    id: 'pdf',
    callback: (doc) => {
      var pageCount = doc.getNumberOfPages()
      doc.deletePage(pageCount)
      doc.save('bordereau')
      doc.output('dataurlnewwindow')
    },
  })
}

export const generateTrame = async () => {
  const doc = new jsPDF('p', 'pt', 'a4', true)

  const pdf = document.getElementById('trame') as HTMLElement

  await doc.html(pdf, {
    callback: (doc) => {
      var pageCount = doc.getNumberOfPages()
      doc.deletePage(pageCount)
      doc.save('trame')
      doc.output('dataurlnewwindow')
    },
    margin: [0, 0, 0, 0],
    x: 0,
    y: 0,
  })

  // doc.setFontSize(10)
  // doc.html(
  //   `<div style={{position: 'absolute', max-width: '400px', top: '130px', left: '150px'}}>FONCIERE OTELLO C/O FDR PROPER Dtion Hotel IBIS\n4 rue de Cassis\n13008 Marseille</div>`,
  //   {
  //     callback: (doc) => {
  //       doc.output('dataurlnewwindow')
  //     },
  //   }
  // )
}

export const formatMembreCSOptions = (d4_conseilsyndical: D4_Conseilsyndical[], value: 'mail1' | 'id' = 'mail1') => {
  const nextOptions = []
  const president = d4_conseilsyndical?.[0]?.president
  const presidentSociete = d4_conseilsyndical?.[0]?.persident_societe
  const membresPersonne = d4_conseilsyndical?.[0]?.membres_personne
  const membresSociete = d4_conseilsyndical?.[0]?.membres_societe

  const values = [
    president ?? null,
    presidentSociete ?? null,
    ...(membresPersonne?.map((m) => m.membre) ?? []),
    ...(membresSociete?.map((m) => m.membre) ?? []),
  ].filter((v) => v) as D4_Personne[]

  president &&
    nextOptions.push({
      key: 'Président du cs',
      label: 'Président (personne) du CS',
      options: president?.[value]
        ? [
            {
              value: president?.[value],
              label: president?.nom + ' ' + president?.prenom,
              permalink: president.coproprietaire?.matricules?.[0]?.permalink,
              key: president?.id,
            },
          ]
        : [],
    })
  presidentSociete &&
    nextOptions.push({
      key: 'Président du cs',
      label: 'Président (societe) du CS',
      options: president?.[value]
        ? [
            {
              value: president?.[value],
              label: presidentSociete?.raisonsociale ?? '',
              permalink: presidentSociete.coproprietaire?.matricules?.[0]?.permalink,
              key: president?.id,
            },
          ]
        : [],
    })
  nextOptions.push({
    key: 'membres du cs',
    label: 'Membres du CS',
    options:
      membresPersonne
        ?.filter((m) => m.membre?.[value])
        ?.map((membre) => ({
          value: membre.membre?.[value],
          label: membre.membre?.nom + ' ' + membre.membre?.prenom,
          key: membre.membre?.id,
          permalink: membre.membre?.coproprietaire?.matricules?.[0]?.permalink,
        })) ?? [],
  })
  membresSociete?.length &&
    nextOptions.push({
      key: 'membres s du cs',
      label: 'Membres (societe) du CS',
      options:
        membresSociete
          ?.filter((m) => m.membre?.[value])
          ?.map((membre) => ({
            value: membre.membre?.[value],
            label: membre.membre?.raisonsociale ?? '',
            key: membre.membre?.id,
            permalink: membre.membre?.coproprietaire?.matricules?.[0]?.permalink,
          })) ?? [],
    })

  return [nextOptions, values]
}

export const httpRequest = async (url: string) => await (await fetch(url)).text()

export const formatAddress = (copro: D4_Copropriete) => {
  const { codepostal, typedevoie, complementadresse, numerodanslavoie, ville, voie } = copro?.adresse ?? {}
  const addressString = [
    copro?.nom,
    `${complementadresse}`,
    `${numerodanslavoie ?? ''} ${TYPE_DE_VOIE[typedevoie]} ${voie}`,
    `${codepostal} ${ville}`,
  ]

  return addressString.filter((a) => a).join('\n')
}

export const validateEmail = (email: string) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return re.test(String(email).toLowerCase())
}

export const toFrenchCurrency = (value: number) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(value)
}
